import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingFallback } from '../common/LoadingFallback';
import { ConnectButton } from '@/components/navigation/ConnectButton';
import { motion } from 'framer-motion';

interface PreviewRouteProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

/**
 * PreviewRoute - Shows page content to non-authenticated users with limited scrolling
 * and a login CTA overlay. Authenticated users see the full page.
 */
export const PreviewRoute: React.FC<PreviewRouteProps> = ({ 
  children, 
  title = "Login to interact",
  description = "Create an account to post, like, and connect with the community"
}) => {
  const { user, isLoading, isInitialized } = useAuth();

  // Wait for auth to initialize
  if (!isInitialized) {
    return <LoadingFallback />;
  }
  
  // Show loading state while auth is loading
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  // If authenticated, show full page
  if (user) {
    return <>{children}</>;
  }
  
  // If not authenticated, show preview with login overlay
  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Content with pointer-events-none to prevent interaction */}
      <div className="pointer-events-none select-none opacity-70 blur-[0.5px] overflow-hidden max-h-screen">
        {children}
      </div>
      
      {/* Login overlay - ml-0 md:ml-64 to respect sidebar on desktop */}
      <div className="fixed inset-0 z-50 ml-0 md:ml-64 flex items-center justify-center">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1B]/60 via-[#1A1A1B]/90 to-[#1A1A1B]" />
        
        {/* Login content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 text-center px-4 max-w-md"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            {description}
          </p>
          
          <ConnectButton />
        </motion.div>
      </div>
    </div>
  );
};
